// Normalize Riddled-In Edge Function responses.
//
// Why this exists:
// Edge Functions often evolve. Older deployed versions might return snake_case
// fields (game_id, word_length) while the frontend expects camelCase (gameId, wordLength).
// This helper makes the frontend resilient to those differences.

const pickFirst = (obj, keys) => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const toNonEmptyString = (v) => {
  if (typeof v !== 'string') return '';
  const s = v.trim();
  return s;
};

const toNumber = (v) => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
};

const toBooleanArray = (v) => {
  if (!Array.isArray(v)) return null;
  return v.map((item) => Boolean(item));
};

const normalizeGuessItem = (rawGuess) => {
  if (typeof rawGuess === 'string') {
    const word = toNonEmptyString(rawGuess);
    return {
      ...(word ? { word } : {}),
      isCorrect: false,
    };
  }

  const word = toNonEmptyString(String(pickFirst(rawGuess, ['word', 'guess']) ?? ''));
  const percentage = toNumber(pickFirst(rawGuess, ['percentage', 'pct', 'percent']));
  const isCorrectRaw = pickFirst(rawGuess, ['isCorrect', 'is_correct', 'correct']);
  const createdAt = toNonEmptyString(
    String(pickFirst(rawGuess, ['createdAt', 'created_at']) ?? ''),
  );
  const lettersInWord = toBooleanArray(
    pickFirst(rawGuess, ['lettersInWord', 'letters_in_word', 'letterMask', 'letter_mask']),
  );

  return {
    ...(word ? { word } : {}),
    ...(Number.isFinite(percentage) ? { percentage } : {}),
    isCorrect: Boolean(isCorrectRaw),
    ...(lettersInWord ? { lettersInWord } : {}),
    ...(createdAt ? { createdAt } : {}),
  };
};

export const normalizeRiddledInStart = (raw) => {
  const gameIdRaw = pickFirst(raw, ['gameId', 'game_id', 'id']);
  const riddleRaw = pickFirst(raw, ['riddle', 'prompt', 'clue']);
  const wordLengthRaw = pickFirst(raw, ['wordLength', 'word_length', 'word_len']);
  const costRaw = pickFirst(raw, ['cost', 'gameCost', 'game_cost']);
  const coinsRaw = pickFirst(raw, ['coins', 'balance', 'newBalance', 'new_balance']);

  const gameId = toNonEmptyString(String(gameIdRaw ?? ''));
  const riddle = toNonEmptyString(riddleRaw);
  const wordLength = toNumber(wordLengthRaw);
  const cost = toNumber(costRaw);
  const coins = toNumber(coinsRaw);

  return {
    gameId,
    riddle,
    wordLength,
    // cost/coins are optional in UI, but nice to keep if present
    ...(Number.isFinite(cost) ? { cost } : {}),
    ...(Number.isFinite(coins) ? { coins } : {}),
  };
};

export const normalizeRiddledInResume = (raw) => {
  const base = normalizeRiddledInStart(raw);
  const status = toNonEmptyString(pickFirst(raw, ['status', 'gameStatus', 'game_status']));
  const word = toNonEmptyString(pickFirst(raw, ['word', 'secret_word', 'secretWord']));
  const guesses = Array.isArray(raw?.guesses)
    ? raw.guesses.map((guess) => normalizeGuessItem(guess))
    : [];

  return {
    ...base,
    ...(status ? { status } : {}),
    ...(word ? { word } : {}),
    guesses,
  };
};

export const normalizeRiddledInGuess = (raw) => {
  const percentageRaw = pickFirst(raw, ['percentage', 'pct', 'percent']);
  const isCorrectRaw = pickFirst(raw, ['isCorrect', 'is_correct', 'correct']);
  const status = toNonEmptyString(pickFirst(raw, ['status', 'gameStatus', 'game_status']));
  const wordLengthRaw = pickFirst(raw, ['wordLength', 'word_length', 'word_len']);
  const lettersInWord = toBooleanArray(
    pickFirst(raw, ['lettersInWord', 'letters_in_word', 'letterMask', 'letter_mask']),
  );

  const percentage = toNumber(percentageRaw);
  const isCorrect = Boolean(isCorrectRaw);
  const wordLength = toNumber(wordLengthRaw);

  return {
    percentage,
    isCorrect,
    ...(lettersInWord ? { lettersInWord } : {}),
    ...(Number.isFinite(wordLength) ? { wordLength } : {}),
    ...(status ? { status } : {}),
  };
};

export const normalizeRiddledInReveal = (raw) => {
  const word = toNonEmptyString(pickFirst(raw, ['word', 'secret_word', 'secretWord']));
  const status = toNonEmptyString(pickFirst(raw, ['status', 'gameStatus', 'game_status']));
  const wordLengthRaw = pickFirst(raw, ['wordLength', 'word_length', 'word_len']);
  const wordLength = toNumber(wordLengthRaw);

  return {
    word,
    ...(Number.isFinite(wordLength) ? { wordLength } : {}),
    ...(status ? { status } : {}),
  };
};

